import { FC, useEffect, useRef } from 'react';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';

import { ReactComponent as FirstInIcon } from 'assets/icons/firstin-icon.svg';
import { ReactComponent as PhoneIphoneIcon } from 'assets/icons/phone_iphone.svg';
import { ReactComponent as BirthdayIcon } from 'assets/icons/cake.svg';
import { ReactComponent as ServiceIcon } from 'assets/icons/service.svg';
import { ReactComponent as TimeIcon } from 'assets/icons/time-line.svg';
import { ReactComponent as PractitionerIcon } from 'assets/icons/practitioners-icon.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as MailOutlineIcon } from 'assets/icons/mail_outline.svg';
import { ReactComponent as EventSeatIcon } from 'assets/icons/event_seat.svg';
import { ReactComponent as SplitScheduleIcon } from 'assets/icons/call_split.svg';
import { ReactComponent as MarkerIcon } from 'assets/icons/google-location.svg';
import {
  ERROR_REASON,
  ERROR_REASON_MESSAGE,
  INSURANCE_PAY_FOR_MY_SELF,
  PMS_NAME,
  PMS_PENDING_ERROR_MESSAGE,
  PMS_TYPE,
} from 'utils/constants';
import { DATE_TIME_FORMAT, datetimeFormat } from 'utils/datetime';
import useAppointmentDetail from 'hooks/useAppointmentDetail';
import AppointmentDetailStatus from 'components/AppointmentDetailModal/AppointmentDetailStatus/AppointmentDetailStatus';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import {
  calculateAge,
  capitalizeAvatarName,
  concatFullName,
  getErrorReason,
  getIsWarningIconEnabled,
  getPostCodeOrZipTextBasedOnCountry,
} from 'utils/common';
import { formatFullPhoneNumber } from 'utils/formatPhoneNumber';
import ErrorReason from 'components/ErrorReason/ErrorReason';
import { getDentistWorkingHour } from 'utils/getDentistWorkingHour';
import {
  getDoctorInfo,
  getHygienistInfo,
  getIsDoctorStartBeforeHygienist,
} from './utils/getPractitionerDetail';
import getPractitionerName from 'utils/getPractitionerName';
import { MINUTE_OF_A_BLOCK } from 'utils/getTimeBlockFromTImeString';
import { getPatientInfoDetail } from './utils/getPatientDetail';
import ExpandWrapper from 'components/ExpandWrapper/ExpandWrapper';
import useClinic from 'hooks/useClinic';

interface CardDetailProps {
  appointmentId: string;
  onClose: () => void;
  className?: string;
}

const CardDetail: FC<CardDetailProps> = ({
  appointmentId,
  onClose,
  className,
}) => {
  const { data: clinic } = useClinic();

  const { data, isLoading } = useAppointmentDetail(appointmentId);

  const containerRef = useRef<HTMLDivElement>(null);

  const errorReason = data ? getErrorReason(data) : null;

  const isWarningIconEnabled = data ? getIsWarningIconEnabled(data) : null;

  const patient = data
    ? getPatientInfoDetail(data.patient, data.pmsPatient)
    : null;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  });

  useEffect(() => {
    const handlePressEscapeButton = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      onClose();
    };

    window.addEventListener('keyup', handlePressEscapeButton);

    return () => {
      window.removeEventListener('keyup', handlePressEscapeButton);
    };
  }, [onClose]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const containerElement = containerRef.current;
      if (!containerElement) return;
      const isUserClickInsideContainer = containerElement.contains(
        e.target as Node
      );
      if (!isUserClickInsideContainer) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [onClose]);

  const renderDentistHygienistInfo = () => {
    if (data.linkedAppointment) {
      return (
        <>
          <button data-for={data.id} data-tip="">
            <SplitScheduleIcon className="all-child:all-child:fill-[#4D97E1]" />
          </button>
          <ReactTooltip
            id={data.id}
            place="bottom"
            effect="solid"
            className="!bg-white !rounded-[0.8rem] !min-w-[22.4rem] !h-[auto] z-[9999] flex !opacity-100 shadow-elevation-07 after:!hidden !px-1.6 !py-0.7 !border-none"
            offset={{ right: 70 }}
          >
            <div className="text-11 text-darkest-grey flex flex-col font-normal leading-[1.8rem]">
              <h3 className="font-bold">Split Appointment</h3>
              <div
                className={`flex flex-col ${
                  getIsDoctorStartBeforeHygienist(data)
                    ? ''
                    : 'flex-col-reverse'
                }`}
              >
                <span>Doctor: {getDoctorInfo(data)}</span>
                <span>Hygienist: {getHygienistInfo(data)} </span>
              </div>
            </div>
          </ReactTooltip>
        </>
      );
    }

    if (data.isSplitScheduling) {
      return (
        <>
          <button data-for={data.id} data-tip="">
            <SplitScheduleIcon className="all-child:all-child:fill-[#4D97E1]" />
          </button>
          <ReactTooltip
            id={data.id}
            place="bottom"
            effect="solid"
            className="!bg-white !rounded-[0.8rem] !min-w-[22.4rem] !h-[auto] z-[9999] flex !opacity-100 shadow-elevation-07 after:!hidden !px-1.6 !py-0.7 !border-none"
            offset={{ right: 70 }}
          >
            <div className="text-11 text-darkest-grey flex flex-col font-normal leading-[1.8rem]">
              <h3 className="font-bold">Split Appointment</h3>
              <div className="flex flex-col-reverse">
                <span>Doctor: {getDentistWorkingHour(data)}</span>
                <span>Hygienist: {getHygienistInfo(data)} </span>
              </div>
            </div>
          </ReactTooltip>
        </>
      );
    }

    return null;
  };

  const renderErrorReason = () => {
    const field =
      data.pmsName === PMS_NAME.CURVE ? PMS_NAME.CURVE : PMS_NAME.OTHER;

    if (isWarningIconEnabled) {
      return (
        <div className="flex gap-x-1 items-center">
          <ErrorReason
            errorReason={ERROR_REASON.WARNING_PENDING}
            className="!w-2 !h-2 shrink-0 self-start"
          />
          <span>{PMS_PENDING_ERROR_MESSAGE[field]}</span>
        </div>
      );
    }

    if (errorReason) {
      return (
        <div className="flex gap-x-1 items-center">
          <ErrorReason
            errorReason={errorReason}
            className="!w-2 !h-2 all-child:fill-secondary-red all-child:all-child:fill-secondary-red"
          />
          <span>{ERROR_REASON_MESSAGE[errorReason]}</span>
        </div>
      );
    }
  };

  const renderInsuranceInfo = (insuranceCoverage: {
    id: string;
    insuranceCarrierName: string;
    memberId: string;
    groupNumberId: string;
    subscriberDob: string;
    subscriberFullName: string;
    employerName: string;
    relationToSubscriber: string;
    insuranceFront: string | null;
    insuranceBack: string | null;
  }) => {
    const excludeFields = ['id', 'insuranceCarrierName'];

    const isInsuranceInfo =
      insuranceCoverage.insuranceFront || insuranceCoverage.insuranceBack;

    const fields = [
      { key: 'memberId', label: 'Member ID' },
      { key: 'groupNumberId', label: 'Group ID' },
      { key: 'subscriberFullName', label: `Subscriber's Full Name` },
      { key: 'subscriberDob', label: `Subscriber's DOB` },
      { key: 'relationToSubscriber', label: `Relation to Subscriber` },
      { key: 'employerName', label: `Employer Name` },
    ];

    if (isInsuranceInfo) {
      return (
        <div className="flex gap-x-1.2 min-h-[9rem]">
          {insuranceCoverage.insuranceFront && (
            <div className="w-16">
              <a
                href={insuranceCoverage.insuranceFront}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={insuranceCoverage.insuranceFront}
                  alt="Insurance Front"
                  className="max-w-full h-auto rounded-[0.8rem] border border-light-grey aspect-[16/9]"
                />
              </a>
            </div>
          )}
          {insuranceCoverage.insuranceBack && (
            <div className="w-16">
              <a
                href={insuranceCoverage.insuranceBack}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={insuranceCoverage.insuranceBack}
                  alt="Insurance Back"
                  className="max-w-full h-auto rounded-[0.8rem] border border-light-grey aspect-[16/9]"
                />
              </a>
            </div>
          )}
        </div>
      );
    }
    return (
      <ExpandWrapper
        className="mt-1.6 mb-0.8 all-child:items-end"
        iconClassName="w-[20px] h-[20px] all-child:fill-mid-grey"
        header={
          <div className="text-12 font-semibold text-mid-grey truncate w-full max-w-[24rem]">
            {insuranceCoverage.insuranceCarrierName}
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-x-1.6 gap-y-1.2 rounded-[0.8rem] border border-light-grey py-1.2 px-1.6 bg-[#FCFCFC] mt-1.1">
          {fields.map(({ key, label }: { key: string; label: string }) => {
            if (excludeFields.includes(key)) {
              return null;
            }

            let value = '-';

            if ((insuranceCoverage as any)[key]) {
              if (key === 'subscriberDob') {
                value = datetimeFormat({
                  dateString: insuranceCoverage[key],
                  pattern: DATE_TIME_FORMAT.DOB,
                });
              } else {
                value = (insuranceCoverage as any)[key];
              }
            }

            return (
              <div className="flex gap-x-0.5">
                <span className="font-bold text-12 min-w-[150px]">
                  {label}:{' '}
                </span>
                <span className="text-14 words-break">{value}</span>
              </div>
            );
          })}
        </div>
      </ExpandWrapper>
    );
  };

  const renderProcedures = () => {
    const syncedPmsProcedures = data.syncedPmsProcedures || [];

    if (syncedPmsProcedures.length) {
      const procedureCodes = syncedPmsProcedures.map((item: any) => (
        <div>{`${item.code} - ${item.name}`}</div>
      ));

      return (
        <div className="mt-1.2 words-break whitespace-pre-line">
          {procedureCodes}
        </div>
      );
    }

    return '-';
  };

  const renderDescriptors = () => {
    const syncedPmsDescriptors = data.syncedPmsDescriptors || [];

    if (syncedPmsDescriptors.length) {
      const descriptors = syncedPmsDescriptors.map((item: any) => (
        <div>{`${item.code} - ${item.name} - ${item.laymanDescription}`}</div>
      ));

      return (
        <div className="mt-1.2 words-break whitespace-pre-line">
          {descriptors}
        </div>
      );
    }

    return '-';
  };

  return (
    <div
      className={`absolute z-[100] rounded-[1.6rem] p-3 shadow-elevation-08 bg-white -translate-y-[70%] w-[42rem] h-[56rem] overflow-auto scrollbar ${className}`}
      ref={containerRef}
    >
      <button onClick={onClose} className="top-3 right-3 absolute">
        <CloseIcon />
      </button>
      {isLoading ? (
        <div className="w-full h-full flex justify-center items-center">
          <LoadingSpinner className="all-child:fill-magenta" />
        </div>
      ) : (
        <>
          <section className="flex gap-x-1.2">
            {data.pmsType === PMS_TYPE.FIRST_IN && (
              <FirstInIcon className="w-2.4 h-2.4" />
            )}
            {renderDentistHygienistInfo()}
            <AppointmentDetailStatus appointment={data} />
          </section>

          <section className="flex mt-1.6 gap-x-2.4 items-center">
            <div className="w-6 h-6 bg-magenta rounded-full flex items-center justify-center text-white text-16 shrink-0">
              {patient &&
                capitalizeAvatarName(
                  concatFullName({
                    firstName: patient.firstName,
                    lastName: patient.lastName,
                  })
                )}
            </div>
            <div className="flex flex-1 flex-col pt-1.2">
              <span className="text-18 leading-[2.7rem] font-bold">
                {patient
                  ? `${patient.name}${
                      calculateAge(patient.dob, 'MMM DD, YYYY')
                        ? `, ${calculateAge(patient.dob, 'MMM DD, YYYY')}`
                        : ''
                    }`
                  : '-'}
              </span>
              <span className="text-12 leading-[1.8rem]">
                Requested on:{' '}
                {moment(data.createdAt).format('MMM DD, YYYY hh:mm A')}
              </span>
            </div>
          </section>

          <main className="mt-3 flex flex-col gap-y-2.4">
            <section className="space-y-1">
              <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey mb-1.2">
                Patient Info
              </h3>
              <div className="flex gap-x-1 items-center">
                <MailOutlineIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
                <span>{patient?.email || '-'}</span>
              </div>

              <div className="flex gap-x-1 items-center">
                <PhoneIphoneIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
                <span>
                  {patient?.phoneNumber
                    ? formatFullPhoneNumber(`+${patient.phoneNumber}`)
                    : '-'}
                </span>
              </div>

              <div className="flex gap-x-1 items-center">
                <BirthdayIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
                <span>
                  {patient?.dob
                    ? datetimeFormat({
                        dateString: patient.dob,
                        pattern: DATE_TIME_FORMAT.DOB,
                      })
                    : '-'}
                </span>
              </div>

              {patient?.homeAddress && (
                <div className="flex gap-x-1 items-center">
                  <MarkerIcon className="w-2 h-2 all-child:fill-mid-grey shrink-0 self-start" />
                  <span className="inline-block max-w-[80%]">
                    {patient.homeAddress}
                    {patient.postalCode
                      ? ` - ${getPostCodeOrZipTextBasedOnCountry(
                          clinic.location?.country
                        )}: ${patient.postalCode}`
                      : ''}
                  </span>
                </div>
              )}
            </section>
            <section className="space-y-1">
              <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey mb-1.2">
                Insurance
              </h3>
              {data.insuranceCoverage ? (
                renderInsuranceInfo(data.insuranceCoverage)
              ) : (
                <span>
                  {data.insuranceName === INSURANCE_PAY_FOR_MY_SELF
                    ? 'Patient self-pay'
                    : '-'}
                </span>
              )}
            </section>
            <section className="space-y-1">
              <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey mb-1.2">
                Booking Details
              </h3>
              <div className="flex gap-x-1 items-center mt-0.2">
                <ServiceIcon className="w-2 h-2 all-child:fill-mid-grey" />
                <span>{data.service?.name || '-'}</span>
              </div>
              <div className="flex gap-x-1 items-center">
                <TimeIcon className="w-2 h-2 all-child:fill-mid-grey" />
                <span>
                  {datetimeFormat({
                    dateString: data.appointmentDate,
                    pattern: 'MMM DD, YYYY',
                  })}{' '}
                  {data.startTime} {' - '}
                  {moment(data.startTime, 'hh:mm A')
                    .add(data.duration * MINUTE_OF_A_BLOCK, 'minute')
                    .format('hh:mm A')}
                </span>
              </div>
              <div className="flex gap-x-1 items-center">
                <PractitionerIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
                <span>{getPractitionerName(data.doctor)}</span>
                {data.doctor?.foreignId && (
                  <span>{`(${data.doctor.foreignId})`}</span>
                )}
              </div>
              <div className="flex gap-x-1 items-center">
                <EventSeatIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
                <span>{data.operatory.name}</span>
              </div>
              {renderErrorReason()}

              {data.externalId && (
                <div className="flex gap-x-1 items-center">
                  <span>Appt ID:</span>
                  <span>{data.externalId}</span>
                </div>
              )}
            </section>
            <section className="space-y-1">
              <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey mb-1.2">
                Last appointment
              </h3>
              <span>
                {data.lastPastAppointment
                  ? `${datetimeFormat({
                      dateString: data.lastPastAppointment.appointmentDate,
                      pattern: 'MMM DD, YYYY',
                    })} - ${getPractitionerName(
                      data.lastPastAppointment.doctor
                    )}`
                  : '-'}
              </span>
            </section>
            <section className="space-y-1">
              <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey mb-1.2">
                Next appointment
              </h3>
              <span>
                {data.nextFutureAppointment
                  ? `${datetimeFormat({
                      dateString: data.nextFutureAppointment.appointmentDate,
                      pattern: 'MMM DD, YYYY',
                    })} - ${getPractitionerName(
                      data.nextFutureAppointment.doctor
                    )}`
                  : '-'}
              </span>
            </section>
            <section className="space-y-1">
              <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey mb-1.2">
                Procedures
              </h3>
              {renderProcedures()}
            </section>
            <section className="space-y-1">
              <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey mb-1.2">
                Descriptors
              </h3>
              {renderDescriptors()}
            </section>

            <section className="space-y-1">
              <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey mb-1.2">
                Patient Notes
              </h3>
              <span>
                {data.patientNote ? (
                  <p className="mt-1.2 words-break whitespace-pre-line">
                    {data.patientNote}
                  </p>
                ) : (
                  '-'
                )}
              </span>
            </section>
            <section className="space-y-1">
              <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey mb-1.2">
                PMS Notes
              </h3>
              <span>
                {data.pmsNote ? (
                  <p className="mt-1.2 whitespace-pre-line">{data.pmsNote}</p>
                ) : (
                  '-'
                )}
              </span>
            </section>
          </main>
        </>
      )}
    </div>
  );
};

export default CardDetail;
